import { useRef, useState, useEffect } from 'react';
import useEnhancedEffect from '../useEnhancedEffect/useEnhancedEffect';
import usePrevious from '../usePrevious/usePrevious';
import type { UseInView, UseInViewState } from './types';
import { observe } from './utils';

const useInView: UseInView = ({
  threshold,
  delay,
  trackVisibility,
  rootMargin,
  root,
  triggerOnce,
  skip,
  initialInView,
  fallbackInView,
  onChange
} = {}) => {
  const ref = useRef<NonNullable<typeof root>>(null);

  const [state, setState] = useState<UseInViewState>({
    inView: !!initialInView,
    entry: undefined
  });

  useEnhancedEffect(() => {
    if (skip || !ref.current) {
      return;
    }

    let unobserve: (() => void) | undefined;

    unobserve = observe(
      ref.current,
      (inView, entry) => {
        setState({
          inView,
          entry
        });

        onChange?.(inView, entry);

        if (entry.isIntersecting && triggerOnce && unobserve) {
          unobserve();
          unobserve = undefined;
        }
      },
      {
        root,
        rootMargin,
        threshold,
        trackVisibility,
        delay
      } as IntersectionObserverInit,
      fallbackInView
    );

    return () => {
      if (unobserve) {
        unobserve();
      }
    };
  }, [
    Array.isArray(threshold) ? threshold.toString() : threshold,
    ref,
    root,
    rootMargin,
    triggerOnce,
    skip,
    trackVisibility,
    fallbackInView,
    delay
  ]);

  const entryTarget = state.entry?.target;

  const previousEntryTarget = usePrevious(state.entry?.target);

  useEffect(() => {
    if (!ref && entryTarget && !triggerOnce && !skip && previousEntryTarget !== entryTarget) {
      setState({
        inView: !!initialInView,
        entry: undefined
      });
    }
  }, [initialInView, triggerOnce, skip, entryTarget, previousEntryTarget]);

  return {
    ref,
    inView: state.inView,
    entry: state.entry
  };
};

export default useInView;
